/**
 * Pure helper functions for the Accounting Rules module.
 *
 * Everything in here is stateless and side-effect free. These
 * helpers are shared between the page composer, hooks, and
 * display components. When the backend ships, nothing in this
 * file changes; only mock data in rulesMockData.ts is swapped
 * for API responses.
 */

import type {
  Rule,
  RuleFilter,
  RuleSortField,
  RuleSortDirection,
  RuleAppliedTo,
  RuleCondition,
} from "./rulesTypes";
import {
  BANK_BRAND_COLORS,
  INTEGRATION_SOURCE_COLORS,
  CONDITION_KEY_LABELS,
  OPERATOR_LABELS,
} from "./rulesMockData";

/**
 * Filter rules by search query and status filter.
 * Search matches rule name, applied-to label, and condition values.
 */
export function filterRules(rules: Rule[], filter: RuleFilter): Rule[] {
  const q = filter.search.trim().toLowerCase();

  return rules.filter((rule) => {
    if (filter.status === "active" && rule.status !== "active") return false;
    if (filter.status === "paused" && rule.status !== "paused") return false;

    if (!q) return true;
    return matchesSearch(rule, q);
  });
}

/**
 * Whether a rule's name, applied-to label, or condition values
 * contain the lowercased search query.
 */
export function matchesSearch(rule: Rule, lowerQuery: string): boolean {
  if (rule.name.toLowerCase().includes(lowerQuery)) return true;
  if (getAppliedToLabel(rule).toLowerCase().includes(lowerQuery)) return true;

  for (const cond of rule.conditions) {
    const condStr = `${CONDITION_KEY_LABELS[cond.key]} ${OPERATOR_LABELS[cond.operator]} ${cond.value}`;
    if (condStr.toLowerCase().includes(lowerQuery)) return true;
  }

  return false;
}

/**
 * Sort rules by the given field and direction. Returns a new array;
 * does not mutate the input.
 */
export function sortRules(
  rules: Rule[],
  sortBy: RuleSortField,
  direction: RuleSortDirection,
): Rule[] {
  const multiplier = direction === "asc" ? 1 : -1;
  const sorted = [...rules];

  sorted.sort((a, b) => {
    switch (sortBy) {
      case "priority":
        return (a.priority - b.priority) * multiplier;
      case "name":
        return a.name.localeCompare(b.name) * multiplier;
      case "matchCount":
        return (a.matchCount - b.matchCount) * multiplier;
      case "lastApplied": {
        const aTime = a.lastAppliedAt ? new Date(a.lastAppliedAt).getTime() : 0;
        const bTime = b.lastAppliedAt ? new Date(b.lastAppliedAt).getTime() : 0;
        return (aTime - bTime) * multiplier;
      }
      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * Format an ISO date string as a short relative time.
 * Returns "just now", "2 hr ago", "yesterday", "3 days ago",
 * "2 weeks ago", or "never" when the date is null.
 */
export function formatLastApplied(dateString: string | null): string {
  if (!dateString) return "never";

  const then = new Date(dateString).getTime();
  const now = Date.now();
  const diffMs = now - then;
  if (diffMs < 0) return "just now";

  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);

  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hr ago`;
  if (days === 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (weeks === 1) return "last week";
  if (weeks < 5) return `${weeks} weeks ago`;
  if (months === 1) return "last month";
  return `${months} months ago`;
}

/**
 * Counts for the Bank rules / Integration rules tab badges and
 * the status filter chips (unused chips removed in later PR).
 */
export function getRuleCountsByStatus(rules: Rule[]): {
  all: number;
  active: number;
  paused: number;
} {
  const active = rules.filter((r) => r.status === "active").length;
  const paused = rules.filter((r) => r.status === "paused").length;
  return { all: rules.length, active, paused };
}

/**
 * Next priority number for a new rule (max existing + 1).
 * Returns 1 when there are no rules yet.
 */
export function getNextPriority(rules: Rule[]): number {
  if (rules.length === 0) return 1;
  return Math.max(...rules.map((r) => r.priority)) + 1;
}

/**
 * Human-readable label for a rule's Applied-to value, shown in the
 * "Applied to" chip in the table and mobile card.
 */
export function getAppliedToLabel(rule: Rule): string {
  const a = rule.appliedTo;
  switch (a.type) {
    case "all_bank_accounts":
      return "All bank accounts";
    case "specific_bank_account":
      return a.bankAccountName;
    case "integration_source":
      return a.integrationName;
  }
}

/**
 * Hex color for the dot inside the "Applied to" chip. Uses the
 * brand color for specific banks and integrations; ink for the
 * "All bank accounts" case.
 */
export function getAppliedToDotColor(rule: Rule): string {
  const a = rule.appliedTo;
  switch (a.type) {
    case "all_bank_accounts":
      return "#0E1A1A";
    case "specific_bank_account":
      return BANK_BRAND_COLORS[a.bankBrand];
    case "integration_source":
      return INTEGRATION_SOURCE_COLORS[a.integrationSource];
  }
}

/**
 * Compact description of a single condition, used by RuleRow and
 * the mobile RuleCard. Returns the parts separately so the UI can
 * style the key (muted), operator (muted), and value (ink mono).
 */
export function describeCondition(cond: RuleCondition): {
  keyLabel: string;
  operatorLabel: string;
  valueLabel: string;
} {
  return {
    keyLabel: CONDITION_KEY_LABELS[cond.key],
    operatorLabel: OPERATOR_LABELS[cond.operator],
    valueLabel: formatConditionValue(cond),
  };
}

/**
 * Format a condition's value for display. Amounts get a dollar
 * sign; "between" shows both bounds; everything else stringifies.
 */
export function formatConditionValue(cond: RuleCondition): string {
  if (cond.operator === "between" && cond.value2 !== undefined) {
    return `${formatSingleValue(cond.key, cond.value)} and ${formatSingleValue(cond.key, cond.value2)}`;
  }
  return formatSingleValue(cond.key, cond.value);
}

function formatSingleValue(
  key: RuleCondition["key"],
  value: string | number,
): string {
  if (key === "amount" || key === "orderTotal") {
    const num = typeof value === "number" ? value : parseFloat(String(value));
    if (!isNaN(num)) return `$${num.toLocaleString("en-CA")}`;
  }
  return String(value);
}

/**
 * Short Settings-column summary for a rule (comma-separated labels).
 * Used for truncated views and search haystack.
 */
export function describeSettings(rule: Rule): string[] {
  const out: string[] = [];
  const a = rule.actions;
  if (a.category) out.push(a.category);
  if (a.account) out.push(`Deposit ${a.account}`);
  if (a.class) out.push(a.class);
  if (a.location) out.push(a.location);
  if (a.reimbursable) out.push("Reimbursable");
  if (a.billable) out.push("Billable");
  if (a.gstTreatment === "included") out.push("GST included");
  if (a.gstTreatment === "zero_rated") out.push("Zero-rated");
  if (a.gstTreatment === "not_applicable") out.push("No GST");
  if (a.gstTreatment === "add_gst") out.push("Add GST");
  if (a.memo) out.push(`Memo: ${a.memo}`);
  return out;
}